import * as React from 'react'
import { Plus } from 'lucide-react'

import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import type { QuoteAddonOfferFormValue } from '@/components/quotes/quote-addon-offers-table'
import type { TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { formatTaxRate } from '@/lib/currency-utils'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select'
import { TaxRateCreateSheet } from '@/components/settings/tax-rate-create-sheet'

const ZERO_UUID = '00000000-0000-0000-0000-000000000000'

interface QuickTaxSelectorProps {
  items: QuoteLineItem[]
  addonOffers?: QuoteAddonOfferFormValue[]
  taxRates: TaxRate[]
  organizationId: string
  onItemsChange: (items: QuoteLineItem[]) => void
  onAddonOffersChange?: (offers: QuoteAddonOfferFormValue[]) => void
  disabled?: boolean
  /**
   * Controlled selection. When provided (with onValueChange), the caller owns
   * the selected tax rate so it can persist across renders — e.g. to auto-apply
   * it to line items added after the bulk apply. Uncontrolled (internal state)
   * when omitted.
   */
  value?: string
  onValueChange?: (value: string) => void
}

/**
 * QuickTaxSelector - Bulk apply tax rate to all line items (and, when
 * `addonOffers`/`onAddonOffersChange` are provided, all add-on offers)
 *
 * Features:
 * - Select dropdown with available tax rates
 * - ZERO_UUID pattern for "No Tax"
 * - Auto-populates all items (and add-ons) with selected rate
 * - Opens TaxRateCreateSheet for adding new rates
 */
export function QuickTaxSelector({
  items,
  addonOffers,
  taxRates,
  organizationId,
  onItemsChange,
  onAddonOffersChange,
  disabled = false,
  value,
  onValueChange,
}: QuickTaxSelectorProps) {
  const [internalTaxId, setInternalTaxId] = React.useState<string>('')
  const quickSelectTaxId = value ?? internalTaxId
  const setQuickSelectTaxId = onValueChange ?? setInternalTaxId
  const [isTaxRateSheetOpen, setIsTaxRateSheetOpen] = React.useState(false)

  const handleTaxRateChange = (value: string) => {
    setQuickSelectTaxId(value === ZERO_UUID ? '' : value)

    // Auto-populate all line items with this tax rate
    const selectedRate = taxRates.find(r => r.id === value)
    if (selectedRate || value === ZERO_UUID) {
      const taxRate = selectedRate ? selectedRate.percentage / 100 : 0
      const updatedItems = items.map(item => {
        const lineTotalCents = calculateLineTotalCents(item.quantity, item.unitPriceCents)
        const salesTaxCents = value === ZERO_UUID ? 0 : Math.round(lineTotalCents * taxRate)
        return {
          ...item,
          isTaxable: value !== ZERO_UUID,
          salesTaxRateId: value === ZERO_UUID ? undefined : value,
          salesTaxRateName: selectedRate?.name,
          salesTaxRate: value === ZERO_UUID ? undefined : taxRate,
          salesTaxCents,
        }
      })
      onItemsChange(updatedItems)

      // Auto-populate all add-on offers with the same rate, mirroring the
      // per-add-on tax picker in quote-addon-offer-edit-sheet.tsx
      if (addonOffers && onAddonOffersChange) {
        const updatedOffers = addonOffers.map(offer => ({
          ...offer,
          isTaxable: value !== ZERO_UUID,
          salesTaxRateId: value === ZERO_UUID ? undefined : value,
          salesTaxRateName: selectedRate?.name,
          salesTaxRate: value === ZERO_UUID ? undefined : taxRate,
        }))
        onAddonOffersChange(updatedOffers)
      }
    }
  }

  return (
    <>
      <div className="grid min-w-0 gap-3">
        <div>
          <Label htmlFor="sales-tax">Apply Sales Tax to All</Label>
          <p className="text-xs text-muted-foreground mt-1">
            {addonOffers
              ? 'Quickly apply a sales tax rate to all line items and add-ons'
              : 'Quickly apply a sales tax rate to all line items'}
          </p>
        </div>
        <Select
          value={quickSelectTaxId || ZERO_UUID}
          onValueChange={handleTaxRateChange}
          disabled={disabled}
        >
          <SelectTrigger
            id="sales-tax"
            className="w-full min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate"
          >
            <SelectValue placeholder="No Tax" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ZERO_UUID}>No Tax</SelectItem>
            <SelectSeparator />
            {taxRates.map((taxRate) => (
              <SelectItem key={taxRate.id} value={taxRate.id}>
                {taxRate.name} ({formatTaxRate(taxRate.percentage)}%)
              </SelectItem>
            ))}
            <SelectSeparator />
            <div
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsTaxRateSheetOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Tax Rule...
            </div>
          </SelectContent>
        </Select>
      </div>

      <TaxRateCreateSheet
        organizationId={organizationId}
        open={isTaxRateSheetOpen}
        onOpenChange={setIsTaxRateSheetOpen}
      />
    </>
  )
}
